// §4.12's pending document: one adjustment request's own page, at
// /inventory/approvals/{id} (spec 06 §4.12 and §4.11, D-315).
//
// A 202 used to leave the requester one line of text and a link to a LIST.
// This page is where a held adjustment lives for the person who raised it and
// for the person who decides it: the movement in words (which product, which
// batch, where, which way, booked against what), the reason and the note, who
// asked and when, the value where the reader may see it (§4's presence rule),
// the status in a sentence, and the controls the reader may use.
//
// It re-reads while the request is pending: on window focus, and every 30 s
// while the tab is visible — a storekeeper who raised it at the rack learns it
// was approved without reloading.
//
// CONTROLS, mirrored and never trusted:
//   * Withdraw — `inventory.adjust`. The server lets only the requester, a
//     facility_admin or a group_admin withdraw; the client has no self-id feed,
//     so it offers the control to every holder and renders the refusal.
//   * Approve / Reject — `inventory.count.review` AND an admin role. §3.9 gives
//     adjustments no auto tier, so finance (which holds count.review) is always
//     refused by the service; offering it the buttons would be a dead control.
//   A decision that loses a race (409 ADJUSTMENT_REQUEST_STATE_INVALID) re-reads
//   the request and says so, rather than leaving a stale page. An approve the
//   stock no longer covers (409 ADJUSTMENT_REQUEST_STALE_STOCK) re-reads too:
//   the server has already committed the request as rejected (§3.9).

import { enumLabel } from "../../../../lib/enum-label";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Button } from "../../../../components/button";
import { Field } from "../../../../components/field";
import { CardSkeleton, EmptyState, ErrorState } from "../../../../components/states";
import { Banner } from "../../../../components/toast";
import { UnauthorizedError } from "../../../../lib/problem";
import { RequestError, requestIdOf } from "../../../../lib/request";
import { hasLiteralRole } from "../../../../lib/role-codes";
import { AdjustStockLink } from "../components/adjust-stock-link";
import { HeldAdjustment, heldWords, usePayloadRefs } from "../components/held-adjustment";
import {
  Field as InvField,
  InventoryStatus,
  MoneyCell,
  MoneyColumnNote,
} from "../components/inventory-ui";
import {
  useAdjustmentRequest,
  useCancelAdjustmentRequest,
  useDecideAdjustmentRequest,
  usePrincipalIndex,
  useWorkOrderRef,
  type AdjustmentRequest,
} from "../hooks/inventory-queries";
import { useInventoryPermissions } from "../hooks/permissions";
import { useTerm } from "../../../onboarding/hooks/terminology";
import { costPresence } from "../lib/cost";
import { inventoryErrorMessage } from "../lib/errors";
import { formatTimestamp, shortId } from "../lib/measure";
import type { AdjustSearch } from "./search-params";

const PENDING_REFRESH_MS = 30_000;

/** Re-read every 30 s while pending AND the tab is visible (§4.12). */
export function pendingRefetchInterval(
  status: string | undefined,
  visibility: DocumentVisibilityState = document.visibilityState,
): number | false {
  return status === "pending_approval" && visibility === "visible" ? PENDING_REFRESH_MS : false;
}

/** The §3.9 decide tier is an ADMIN act (facility_admin / group_admin). */
const DECIDER_ROLES = ["group_admin", "facility_admin"] as const;

export function AdjustmentRequestScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams({ strict: false });
  const id = typeof params.id === "string" ? params.id : "";
  const request = useAdjustmentRequest(id, true, { refetchInterval: pendingRefetchInterval });

  useEffect(() => {
    if (request.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [request.error, navigate]);

  const head = (
    <PageHeader
      crumbs={[{ label: t("inventory.approvals.title"), to: "/inventory/approvals" }]}
      title={t("inventory.adjustRequest.title")}
    />
  );

  if (request.isPending || request.error instanceof UnauthorizedError) {
    return (
      <section className="adjust-request">
        {head}
        <CardSkeleton lines={6} label={t("inventory.adjustRequest.loading")} />
      </section>
    );
  }
  if (request.error) {
    // §5: a missing request and one in a plant outside the reader's scope are
    // the same 404 — the page must not say which.
    if (request.error instanceof RequestError && request.error.httpStatus === 404) {
      return (
        <section className="adjust-request" data-testid="adjust-request-missing">
          {head}
          <EmptyState
            title={t("inventory.adjustRequest.notFoundTitle")}
            body={t("inventory.adjustRequest.notFoundBody")}
          />
        </section>
      );
    }
    return (
      <section className="adjust-request">
        {head}
        <ErrorState
          title={t("inventory.adjustRequest.errorTitle")}
          detail={request.error.message}
          retryLabel={t("inventory.common.retry")}
          onRetry={() => void request.refetch()}
          requestId={requestIdOf(request.error)}
          requestIdLabel={t("inventory.common.requestId")}
        />
      </section>
    );
  }
  return (
    <section className="adjust-request" data-testid="adjust-request">
      {head}
      <RequestBody request={request.data.request} onStale={() => void request.refetch()} />
    </section>
  );
}

function RequestBody({ request, onStale }: { request: AdjustmentRequest; onStale: () => void }) {
  const { t, i18n } = useTranslation();
  const { term } = useTerm();
  const perms = useInventoryPermissions();
  const refs = usePayloadRefs([request.movement_payload]);
  const principals = usePrincipalIndex(perms.canReadMembers);
  const decide = useDecideAdjustmentRequest();
  const cancel = useCancelAdjustmentRequest();
  const [acting, setActing] = useState<"approve" | "reject" | "cancel" | null>(null);
  const [note, setNote] = useState("");
  const [stateChanged, setStateChanged] = useState(false);

  const words = heldWords(t, term("lot"), i18n.language, request.movement_payload, refs);
  const who = (principal: string | null): string =>
    principal === null ? "—" : (principals.byId.get(principal) ?? shortId(principal));
  const when = (at: string | null): string =>
    at === null ? "—" : formatTimestamp(at, i18n.language);
  const presence = costPresence([request], (row) => row.est_value_minor);
  const pending = request.status === "pending_approval";
  const canCancel = perms.has("inventory.adjust");
  const canDecide =
    perms.has("inventory.count.review") &&
    // LITERAL-ROLE-CODES: `adjustments.service.ts` decides on the literal admin codes (approverCapability).
    hasLiteralRole(perms, DECIDER_ROLES);
  const error = decide.error ?? cancel.error;

  function settled(err: unknown) {
    if (!(err instanceof RequestError)) return;
    if (err.code === "ADJUSTMENT_REQUEST_STATE_INVALID") {
      setStateChanged(true);
      setActing(null);
      onStale();
    } else if (err.code === "ADJUSTMENT_REQUEST_STALE_STOCK") {
      // §3.9 commits the request as `rejected` / `stale_stock` and THEN
      // answers 409: the page must re-read, or it keeps offering Approve on a
      // request that is already decided, and hides "record it again".
      setActing(null);
      onStale();
    }
  }

  function submit(action: "approve" | "reject" | "cancel") {
    const trimmed = note.trim();
    setStateChanged(false);
    const done = {
      onSuccess: () => {
        setActing(null);
        setNote("");
      },
      onError: settled,
    };
    if (action === "cancel") {
      cancel.mutate({ id: request.id, ...(trimmed.length > 0 ? { note: trimmed } : {}) }, done);
    } else {
      decide.mutate(
        { id: request.id, action, ...(trimmed.length > 0 ? { note: trimmed } : {}) },
        done,
      );
    }
  }

  const stale = request.status === "rejected" && request.reject_reason === "stale_stock";
  const recordAgain = stale && words !== undefined ? recordAgainSearch(words, request) : undefined;

  return (
    <>
      <div className="inv-panel">
        <p className="adjust-request-words" data-testid="adjust-request-words">
          <HeldAdjustment payload={request.movement_payload} refs={refs} withNote={false} />
        </p>
        <dl className="inv-fields">
          <InvField label={t("inventory.adjustRequest.reason")}>
            {words?.payload.reason === undefined
              ? "—"
              : enumLabel(t, "inventory.vocab.reason", words.payload.reason)}
          </InvField>
          <InvField label={t("inventory.adjustRequest.note")}>
            <span data-testid="adjust-request-note">{words?.payload.note ?? "—"}</span>
          </InvField>
          {request.ref_work_order_id !== null ? (
            <InvField label={t("inventory.adjustRequest.workOrder")}>
              <WorkOrderRefLink id={request.ref_work_order_id} />
            </InvField>
          ) : null}
          <InvField label={t("inventory.adjustRequest.value")}>
            {presence === "visible" ? (
              <MoneyCell
                row={request}
                valueOf={(row) => row.est_value_minor}
                currencyOf={(row) => row.currency}
              />
            ) : (
              <MoneyColumnNote presence={presence} />
            )}
          </InvField>
        </dl>
        <p className="inv-hint">
          {t("inventory.adjustRequest.requested", {
            who: who(request.requested_by),
            when: when(request.requested_at),
          })}
        </p>
        <p data-testid="adjust-request-status" data-status={request.status}>
          <InventoryStatus status={request.status} scope="adjustmentRequest" />{" "}
          {request.status === "pending_approval"
            ? t("inventory.adjustRequest.status.pending_approval", {
                when: when(request.requested_at),
              })
            : request.status === "posted"
              ? t("inventory.adjustRequest.status.posted", {
                  who: who(request.decided_by),
                  when: when(request.resolved_at),
                })
              : request.status === "cancelled"
                ? t("inventory.adjustRequest.status.cancelled", {
                    who: who(request.decided_by),
                    when: when(request.resolved_at),
                  })
                : stale
                  ? t("inventory.adjustRequest.status.stale")
                  : t("inventory.adjustRequest.status.rejected", {
                      who: who(request.decided_by),
                      reason: request.reject_reason ?? "—",
                    })}
        </p>
        {request.status === "posted" ? (
          <Link
            to="/inventory/movements"
            search={{ source_doc_type: "manual_adjustment", source_doc_id: request.id }}
            className="ds-lnk"
            data-testid="adjust-request-ledger"
          >
            {t("inventory.adjustRequest.seeLedger")}
          </Link>
        ) : null}
        {recordAgain !== undefined ? (
          <AdjustStockLink
            search={recordAgain}
            label={t("inventory.adjustRequest.recordAgain")}
            testId="adjust-request-again"
          />
        ) : null}
      </div>

      {stateChanged ? (
        <Banner kind="warn" data-testid="adjust-request-state-changed">
          {t("inventory.adjustRequest.stateChanged")}
        </Banner>
      ) : null}

      {pending && (canDecide || canCancel) ? (
        acting === null ? (
          <div className="row-actions" data-testid="adjust-request-actions">
            {canDecide ? (
              <>
                <Button
                  data-testid="adjust-request-approve"
                  onClick={() => {
                    setActing("approve");
                    setNote("");
                  }}
                >
                  {t("inventory.approvals.approve")}
                </Button>
                <Button
                  data-testid="adjust-request-reject"
                  onClick={() => {
                    setActing("reject");
                    setNote("");
                  }}
                >
                  {t("inventory.approvals.reject")}
                </Button>
              </>
            ) : null}
            {canCancel ? (
              <Button
                data-testid="adjust-request-cancel"
                onClick={() => {
                  setActing("cancel");
                  setNote("");
                }}
              >
                {t("inventory.approvals.cancel")}
              </Button>
            ) : null}
          </div>
        ) : (
          <form
            className="inv-panel"
            data-testid="adjust-request-confirm"
            onSubmit={(event) => {
              event.preventDefault();
              submit(acting);
            }}
          >
            <p className="inv-warning">
              {acting === "approve"
                ? t("inventory.approvals.approveWarning")
                : acting === "reject"
                  ? t("inventory.approvals.rejectWarning")
                  : t("inventory.approvals.cancelWarning")}
            </p>
            <Field
              label={
                acting === "reject"
                  ? t("inventory.approvals.noteRequired")
                  : t("inventory.approvals.note")
              }
            >
              <input
                className="ds-input"
                value={note}
                data-testid="adjust-request-note-input"
                onChange={(event) => {
                  setNote(event.target.value);
                }}
                required={acting === "reject"}
              />
            </Field>
            <div className="form-actions">
              <Button
                type="submit"
                variant="primary"
                data-testid="adjust-request-confirm-submit"
                busy={decide.isPending || cancel.isPending}
                disabled={acting === "reject" && note.trim().length === 0}
              >
                {t("inventory.common.confirm")}
              </Button>
              <Button
                onClick={() => {
                  setActing(null);
                  setNote("");
                }}
              >
                {t("inventory.common.cancel")}
              </Button>
            </div>
          </form>
        )
      ) : null}

      {error !== null && !stateChanged ? (
        <p className="form-error" role="alert" data-testid="adjust-request-error">
          {inventoryErrorMessage(t, error, "inventory.approvals.failed")}
        </p>
      ) : null}
    </>
  );
}

/** A stale request's own payload, back into the form (§4.12 "Record it again"). */
function recordAgainSearch(
  words: NonNullable<ReturnType<typeof heldWords>>,
  request: AdjustmentRequest,
): AdjustSearch | undefined {
  if (words.direction !== "increase" && words.direction !== "decrease") return undefined;
  const payload = words.payload;
  return {
    facility_id: request.facility_id,
    ...(payload.productId !== undefined ? { product_id: payload.productId } : {}),
    ...(payload.lotId !== undefined ? { lot_id: payload.lotId } : {}),
    ...(words.stockLocationId !== undefined ? { location_id: words.stockLocationId } : {}),
    direction: words.direction,
    ...(words.counter !== undefined ? { counter: words.counter } : {}),
    ...(payload.quantity !== undefined ? { quantity: payload.quantity } : {}),
    ...(payload.uom !== undefined ? { uom: payload.uom } : {}),
    ...(payload.reason !== undefined ? { reason: payload.reason } : {}),
    ...(request.ref_work_order_id !== null ? { work_order_id: request.ref_work_order_id } : {}),
    from: "approvals",
  };
}

/** The work order a request is recorded against, by its number where the reader may read it. */
function WorkOrderRefLink({ id }: { id: string }) {
  const { term } = useTerm();
  const workOrder = useWorkOrderRef(id);
  return (
    <Link
      to="/production/work-orders/$id"
      params={{ id }}
      className="ds-lnk"
      data-testid="adjust-request-work-order"
    >
      {`${term("work_order")} ${workOrder.data?.woNo ?? shortId(id)}`}
    </Link>
  );
}
